import {
  type ReactElement,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { AgGridReact } from 'ag-grid-react';
import type { ColDef, GridOptions, ICellRendererParams, IHeaderParams } from 'ag-grid-community';
import { AllCommunityModule, ModuleRegistry } from 'ag-grid-community';
import {
  NeonButton,
  NeonCardStandard,
  NeonCheckboxField,
  NeonLoadingIndicator,
} from '@ps-refarch-ux/neon';
import { DATA_SLOT, NeonButtonTypes } from '@/constants/neon-constants';
import { FilterPanel, type FilterField, type FilterChip } from './filter-panel';
import '@/styles/data-grid.grid.css';
import {
  gridContainerBaseStyle,
  splitLayoutStyle,
  mainContentStyle,
  actionBarStyle,
  actionBarLeftStyle,
  actionBarRightStyle,
} from './data-grid.styles';
import gridStyles from './data-grid.module.scss';

ModuleRegistry.registerModules([AllCommunityModule]);

const DEFAULT_AUTO_HEIGHT_MAX = 'min(600px, 70vh)';

/**
 * With `labelText: ''`, Neon links an empty `<label for>` to the checkbox. Firefox axe
 * still requires a name on the **input** (`aria-label` on the input satisfies `label`).
 */
const DG_CHECKBOX_INPUT_ARIA_ROW = 'Select row';
const DG_CHECKBOX_INPUT_ARIA_SELECT_ALL = 'Select all rows on page';

function useNeonGridCheckboxInputAria(
  shellRef: RefObject<HTMLDivElement | null>,
  ariaLabel: string
): void {
  useLayoutEffect(() => {
    const root = shellRef.current;
    const apply = (): void => {
      if (!root) {
        return;
      }
      for (const input of root.querySelectorAll<HTMLInputElement>('input[type="checkbox"]')) {
        if (!input.id?.startsWith('checkbox-dg-')) {
          continue;
        }
        if (input.getAttribute('aria-label')?.trim()) {
          continue;
        }
        input.setAttribute('aria-label', ariaLabel);
      }
    };
    apply();
    const mo = new MutationObserver(apply);
    if (root) {
      mo.observe(root, {
        childList: true,
        subtree: true,
        attributes: true,
        attributeFilter: ['id', 'type', 'aria-label'],
      });
    }
    const tid = window.setTimeout(apply, 0);
    return () => {
      mo.disconnect();
      window.clearTimeout(tid);
    };
  }, [ariaLabel, shellRef]);
}

// ─── Checkbox column types ────────────────────────────────────────────────────

/** Configuration for the built-in NeonCheckboxField-based checkbox column.
 *  When provided to `DataGrid`, a checkbox column is prepended to the grid
 *  that follows the same pattern used in cta-student-mfe TaskListTable. */
export interface DataGridCheckboxColumn<TRow> {
  /** Derive the unique row key from a row object. */
  getRowId: (row: TRow) => string;
  /** Set of currently selected row IDs. */
  selectedIds: Set<string>;
  /** Toggle a single row's selection by its ID. */
  onToggleRow: (id: string) => void;
  /** Toggle selection for all rows currently visible in the grid (current page). */
  onTogglePage: (ids: string[]) => void;
  /** Disable all checkbox interactions (e.g. while a bulk operation is in-flight). */
  disabled?: boolean;
  /**
   * When true, the header shows a loading indicator instead of the select-all control
   * (e.g. while fetching all row IDs for “select entire catalog”).
   */
  headerSelectAllLoading?: boolean;
  /** Fixed pixel width for the checkbox column (default: 48). */
  width?: number;
}

// ─── Module-level cell/header renderers (stable references, no re-creation) ──

function DataGridCheckboxCellInner({
  rowId,
  isSelected,
  col,
}: {
  rowId: string;
  isSelected: boolean;
  col: DataGridCheckboxColumn<unknown>;
}): ReactElement {
  const shellRef = useRef<HTMLDivElement>(null);
  useNeonGridCheckboxInputAria(shellRef, DG_CHECKBOX_INPUT_ARIA_ROW);
  return (
    <div ref={shellRef} className={gridStyles.checkboxCellWrapper}>
      <NeonCheckboxField
        id={`dg-checkbox-${rowId}`}
        dataLabelText={DG_CHECKBOX_INPUT_ARIA_ROW}
        dataShowLabel={false}
        options={[
          {
            id: `dg-opt-${rowId}`,
            labelText: '',
            name: rowId,
            disabled: col.disabled,
          },
        ]}
        modelValue={isSelected ? [rowId] : []}
        modelValueChange={() => {
          if (!col.disabled) {
            col.onToggleRow(rowId);
          }
        }}
      />
    </div>
  );
}

/** Cell renderer — renders a NeonCheckboxField for each row. Reads from AG Grid context. */
function DataGridCheckboxCell(params: ICellRendererParams): ReactElement | null {
  const { data, context } = params as ICellRendererParams & {
    context: { checkboxColumn?: DataGridCheckboxColumn<unknown> };
  };
  const col = context?.checkboxColumn;
  if (!col || !data) {
    return null;
  }

  const rowId = col.getRowId(data);
  const isSelected = col.selectedIds.has(rowId);

  return <DataGridCheckboxCellInner rowId={rowId} isSelected={isSelected} col={col} />;
}

/** Header renderer — renders a NeonCheckboxField that selects/deselects all visible rows. */
function DataGridCheckboxHeader(params: IHeaderParams): ReactElement | null {
  const { context, api } = params as IHeaderParams & {
    context: { checkboxColumn?: DataGridCheckboxColumn<unknown> };
  };
  const col = context?.checkboxColumn;
  if (!col) {
    return null;
  }

  const rowIds: string[] = [];
  api?.forEachNode?.((node: { data: unknown }) => {
    if (node.data) {
      rowIds.push(col.getRowId(node.data));
    }
  });

  if (rowIds.length === 0) {
    return null;
  }

  if (col.headerSelectAllLoading) {
    return (
      <div
        className={gridStyles.checkboxCellWrapper}
        role="status"
        aria-live="polite"
        aria-label="Loading selection"
      >
        <NeonLoadingIndicator dataSize="small" />
      </div>
    );
  }

  const allSelected = rowIds.every((id) => col.selectedIds.has(id));

  return <DataGridCheckboxHeaderInner col={col} rowIds={rowIds} allSelected={allSelected} />;
}

function DataGridCheckboxHeaderInner({
  col,
  rowIds,
  allSelected,
}: {
  col: DataGridCheckboxColumn<unknown>;
  rowIds: string[];
  allSelected: boolean;
}): ReactElement {
  const shellRef = useRef<HTMLDivElement>(null);
  useNeonGridCheckboxInputAria(shellRef, DG_CHECKBOX_INPUT_ARIA_SELECT_ALL);
  return (
    <div ref={shellRef} className={gridStyles.checkboxCellWrapper}>
      <NeonCheckboxField
        id="dg-select-all-header"
        dataLabelText={DG_CHECKBOX_INPUT_ARIA_SELECT_ALL}
        dataShowLabel={false}
        options={[
          {
            id: 'dg-select-all-opt',
            labelText: '',
            name: 'select-all',
            disabled: col.disabled,
          },
        ]}
        modelValue={allSelected ? ['select-all'] : []}
        modelValueChange={() => {
          if (!col.disabled) {
            col.onTogglePage(rowIds);
          }
        }}
      />
    </div>
  );
}

// ─── DataGrid component ───────────────────────────────────────────────────────

export interface DataGridFilterConfig {
  title: string;
  fields: FilterField[];
  chips?: FilterChip[];
  /** Number of active filters; used for button label (e.g. "Filters (3)"). Optional; falls back to chips.length. */
  filterCount?: number;
  onFieldChange: (fieldId: string, value: unknown) => void;
  onChipRemove?: (chipId: string) => void;
  onClearAllChips?: () => void;
  onApply?: () => void;
  applyText?: string;
  closeButtonId?: string;
  applyButtonId?: string;
  toggleButtonId?: string;
  toggleButtonText?: string;
  toggleButtonIcon?: string;
  /** When true, the Filters toggle is not rendered; use for empty grids with no active filters. */
  hideToggleButton?: boolean;
  /** When true, the left filter rail renders without a close action. */
  hideCloseButton?: boolean;
  /** When true, the full top controls row (Filters area + action bar) is not rendered. */
  hideActionBar?: boolean;
  hasChanges?: boolean;
  defaultFields?: FilterField[];
}

export interface DataGridProps<TRow> {
  id: string;
  columnDefs: ColDef<TRow>[];
  rowData: TRow[];
  domLayout?: 'normal' | 'autoHeight';
  rowHeight?: number;
  headerHeight?: number;
  gridHeight?: string;
  cardWrapper?: boolean;
  emptyState?: ReactNode;
  /** When filters are active (filterCount > 0) but rowData is empty, show this in the content area instead of emptyState. Header (Filters + action bar) stays visible. */
  emptyStateWhenFiltered?: ReactNode;
  /**
   * When true, empty rows are treated like a filtered view: show `emptyStateWhenFiltered` if provided
   * (e.g. course catalog search text with no matches — search is outside the filter drawer).
   */
  emptyRowsTreatAsFiltered?: boolean;
  /** When true, always render filter + action bar layout; when rowData.length === 0 show emptyState in content area (e.g. CC with active filters) */
  alwaysShowLayout?: boolean;
  className?: string;
  gridOptions?: Partial<GridOptions<TRow>>;
  gridRef?: RefObject<AgGridReact<TRow> | null>;
  filter?: DataGridFilterConfig;
  defaultFilterOpen?: boolean;
  isFilterOpen?: boolean;
  onFilterOpenChange?: (open: boolean) => void;
  /** Content to the right of the Filters button (e.g. plan count, search). */
  actionBar?: ReactNode;
  /** Content to the left of actionBar, next to the Filters button (e.g. Hide inactive checkbox). */
  actionBarLeftSupplement?: ReactNode;
  /** Rendered between the action bar and the grid — used for selection status bars
   *  (e.g. "N selected / Select all N / Clear all"). Only rendered when non-null. */
  selectionBar?: ReactNode;
  /** Rendered below the grid inside the main content area (e.g. pagination). */
  children?: ReactNode;
  /** When true and cardWrapper is enabled, renders children inside the card below the grid. */
  childrenInsideCard?: boolean;
  /** When provided, prepends a NeonCheckboxField-based selection column to the grid.
   *  The column is only visible when this prop is supplied — pass `undefined` to hide it
   *  (e.g. for school-level users). */
  checkboxColumn?: DataGridCheckboxColumn<TRow>;
}

export function DataGrid<TRow>({
  id,
  columnDefs,
  rowData,
  domLayout = 'autoHeight',
  rowHeight = 50,
  headerHeight = 50,
  gridHeight,
  cardWrapper = false,
  emptyState,
  emptyStateWhenFiltered,
  emptyRowsTreatAsFiltered = false,
  alwaysShowLayout = false,
  className,
  gridOptions,
  gridRef,
  filter,
  defaultFilterOpen = false,
  isFilterOpen: controlledFilterOpen,
  onFilterOpenChange,
  actionBar,
  actionBarLeftSupplement,
  selectionBar,
  children,
  childrenInsideCard = false,
  checkboxColumn,
}: DataGridProps<TRow>): ReactElement {
  if (import.meta.env.DEV && childrenInsideCard && !cardWrapper && children) {
    console.warn(
      'DataGrid: childrenInsideCard is ignored when cardWrapper is false; children will not render.'
    );
  }

  const [internalFilterOpen, setInternalFilterOpen] = useState(defaultFilterOpen);
  const isFilterOpen = controlledFilterOpen ?? internalFilterOpen;

  const baselineFields = useMemo(
    () => filter?.defaultFields ?? filter?.fields ?? [],
    // Only compute once on mount — defaultFields is the stable baseline
    // eslint-disable-next-line react-hooks/exhaustive-deps
    []
  );

  const countActiveFilters = useMemo(
    () =>
      (current: FilterField[]): number => {
        let count = 0;
        for (let i = 0; i < current.length; i++) {
          const curr = current[i];
          const def = baselineFields[i];
          if (!def || curr.type !== def.type) {
            continue;
          }
          if (curr.type === 'select' && def.type === 'select' && curr.value !== def.value) {
            count++;
          }
          if (
            (curr.type === 'multiselect' || curr.type === 'chipMultiselect') &&
            (def.type === 'multiselect' || def.type === 'chipMultiselect')
          ) {
            const defaultSet = new Set(def.value);
            const added = curr.value.filter((v) => !defaultSet.has(v));
            count += added.length;
          }
          if (curr.type === 'checkbox' && def.type === 'checkbox' && curr.checked !== def.checked) {
            count++;
          }
          if (
            curr.type === 'range' &&
            def.type === 'range' &&
            (curr.fromField.value !== def.fromField.value ||
              curr.toField.value !== def.toField.value)
          ) {
            count++;
          }
        }
        return count;
      },
    [baselineFields]
  );

  const [appliedFilterCount, setAppliedFilterCount] = useState(() =>
    countActiveFilters(filter?.fields ?? [])
  );

  useEffect(() => {
    if (filter?.hasChanges === false) {
      setAppliedFilterCount(countActiveFilters(filter?.fields ?? []));
    }
  }, [filter?.hasChanges, filter?.fields, countActiveFilters]);

  const setIsFilterOpen = useCallback(
    (valueOrUpdater: boolean | ((prev: boolean) => boolean)): void => {
      const next =
        typeof valueOrUpdater === 'function' ? valueOrUpdater(isFilterOpen) : valueOrUpdater;
      if (onFilterOpenChange) {
        onFilterOpenChange(next);
      } else {
        setInternalFilterOpen(next);
      }
    },
    [isFilterOpen, onFilterOpenChange]
  );

  const toggleFilter = useCallback((): void => {
    setIsFilterOpen((prev) => !prev);
  }, [setIsFilterOpen]);

  const closeFilter = useCallback((): void => {
    setIsFilterOpen(false);
  }, [setIsFilterOpen]);

  const handleApply = useCallback((): void => {
    setAppliedFilterCount(countActiveFilters(filter?.fields ?? []));
    filter?.onApply?.();
  }, [filter, countActiveFilters]);

  // ── Checkbox column: prepend to columnDefs when provided ─────────────────
  const effectiveColumnDefs = useMemo<ColDef<TRow>[]>(() => {
    if (!checkboxColumn) {
      return columnDefs;
    }

    const checkboxColDef: ColDef<TRow> = {
      colId: '__checkbox__',
      headerName: '',
      pinned: 'left' as const,
      minWidth: checkboxColumn.width ?? 48,
      maxWidth: checkboxColumn.width ?? 48,
      width: checkboxColumn.width ?? 48,
      sortable: false,
      resizable: false,
      suppressMovable: true,
      suppressHeaderMenuButton: true,
      cellRenderer: DataGridCheckboxCell,
      headerComponent: DataGridCheckboxHeader,
      cellClass: 'dg-checkbox-cell',
      headerClass: 'neon-data-grid-header dg-checkbox-header',
    };

    return [checkboxColDef, ...columnDefs];
  }, [checkboxColumn, columnDefs]);

  // ── Grid context: passes checkboxColumn callbacks to cell/header renderers ─
  const gridContext = useMemo(
    () =>
      checkboxColumn
        ? {
            checkboxColumn: {
              getRowId: checkboxColumn.getRowId,
              selectedIds: checkboxColumn.selectedIds,
              onToggleRow: checkboxColumn.onToggleRow,
              onTogglePage: checkboxColumn.onTogglePage,
              disabled: checkboxColumn.disabled,
              headerSelectAllLoading: checkboxColumn.headerSelectAllLoading,
            } as DataGridCheckboxColumn<unknown>,
          }
        : undefined,
    // selectedIds is a Set — new reference on each update, so this memo recomputes on selection change
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      checkboxColumn?.getRowId,
      checkboxColumn?.selectedIds,
      checkboxColumn?.onToggleRow,
      checkboxColumn?.onTogglePage,
      checkboxColumn?.disabled,
      checkboxColumn?.headerSelectAllLoading,
    ]
  );

  if (emptyState && rowData.length === 0 && !filter && !alwaysShowLayout) {
    return <div style={{ width: '100%' }}>{emptyState}</div>;
  }

  const hasActiveFilters =
    appliedFilterCount > 0 || (filter?.chips?.length ?? 0) > 0 || emptyRowsTreatAsFiltered;
  const isEmpty = rowData.length === 0 && (emptyState || emptyStateWhenFiltered);
  const emptyContent =
    isEmpty && hasActiveFilters && emptyStateWhenFiltered ? emptyStateWhenFiltered : emptyState;

  const gridContent = (
    <div
      style={gridContainerBaseStyle(
        gridHeight,
        !gridHeight && domLayout === 'autoHeight' && rowData.length > 0
          ? DEFAULT_AUTO_HEIGHT_MAX
          : undefined,
        !gridHeight && domLayout === 'autoHeight' && rowData.length > 0
      )}
      className={`ag-theme-quartz data-grid ${id}-grid${className ? ` ${className}` : ''}`}
      data-testid={`${id}-grid`}
    >
      <AgGridReact
        ref={gridRef}
        columnDefs={effectiveColumnDefs}
        rowData={rowData}
        domLayout={domLayout}
        rowHeight={rowHeight}
        headerHeight={headerHeight}
        suppressHorizontalScroll={false}
        suppressMovableColumns={true}
        enableCellTextSelection={true}
        suppressAnimationFrame={true}
        loading={false}
        context={gridContext}
        {...gridOptions}
      />
    </div>
  );

  const wrappedGrid = cardWrapper ? (
    <NeonCardStandard dataInjectionSpacing="false">
      <div data-slot={DATA_SLOT} className={gridStyles.gridCardBody}>
        {gridContent}
        {childrenInsideCard && children}
      </div>
    </NeonCardStandard>
  ) : (
    gridContent
  );

  if (!filter) {
    return (
      <>
        {selectionBar && <div>{selectionBar}</div>}
        {wrappedGrid}
        {!childrenInsideCard && children}
      </>
    );
  }

  const toggleFilterBadgeCount =
    typeof filter.filterCount === 'number' ? filter.filterCount : appliedFilterCount;

  return (
    <div style={splitLayoutStyle}>
      {isFilterOpen && (
        <FilterPanel
          title={filter.title}
          fields={filter.fields}
          chips={filter.chips}
          onFieldChange={filter.onFieldChange}
          onChipRemove={filter.onChipRemove}
          onClearAllChips={filter.onClearAllChips}
          onClose={closeFilter}
          onApply={filter.applyText ? handleApply : undefined}
          applyText={filter.applyText}
          closeButtonId={filter.closeButtonId}
          applyButtonId={filter.applyButtonId}
          hasChanges={filter.hasChanges}
          hideCloseButton={filter.hideCloseButton}
        />
      )}

      <div style={mainContentStyle}>
        {!filter.hideActionBar && (
          <div style={actionBarStyle}>
            <div style={actionBarLeftStyle}>
              {!filter.hideToggleButton && (
                <NeonButton
                  id={filter.toggleButtonId ?? `${id}-filter-toggle`}
                  dataType={NeonButtonTypes.Secondary}
                  dataText={
                    toggleFilterBadgeCount
                      ? `${filter.toggleButtonText ?? 'Filters'} (${toggleFilterBadgeCount})`
                      : (filter.toggleButtonText ?? 'Filters')
                  }
                  dataIcon={filter.toggleButtonIcon ?? 'settings-tune'}
                  onClick={toggleFilter}
                />
              )}
              {actionBarLeftSupplement}
            </div>
            {actionBar !== null && actionBar !== undefined && (
              <div style={actionBarRightStyle}>{actionBar}</div>
            )}
          </div>
        )}

        {selectionBar && <div>{selectionBar}</div>}
        {isEmpty ? emptyContent : wrappedGrid}
        {!childrenInsideCard && children}
      </div>
    </div>
  );
}
